import React, { useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import displayNameScreenshot from '../../../../assets/email/img_display_name_image.jpg';
import descriptionTitleScreenshot from '../../../../assets/email/img_description_title_image.jpg';
import descriptionScreenshot from '../../../../assets/email/img_description_image.jpg';
import descriptionEndingScreenshot from '../../../../assets/email/img_description_ending_image.jpg';
import footerInfoScreenshot from '../../../../assets/email/img_footer_info_image.jpg';
import xlinkScreenshot from '../../../../assets/email/img_x_link_image.jpg';
import linkedinLinkScreenshot from '../../../../assets/email/img_linkedin_link_image.jpg';
import instagramLinkScreenshot from '../../../../assets/email/img_instagram_link_image.jpg';
import { Skeleton } from 'primereact/skeleton';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import OpenAPI from '../../../../endpoint/Openapi';
import { type paths } from '../../../../endpoint/Schema';
import { noop, normalizeString } from '../../../../utils/GeneralUtils';
import { InputTextarea } from 'primereact/inputtextarea';
import { type CustomError } from '../../../../models/Error';

function Emails() {
  const tenantId = useTenantStore(selectTenantId);
  const [emailSettings, setEmailSettings] =
    React.useState<
      paths['/api/v2/admin/email-setting']['get']['responses']['200']['content']['application/json']
    >();
  const [displayName, setDisplayName] = React.useState(() => '');
  const [descriptionTitle, setDescriptionTitle] = React.useState(() => '');
  const [description, setDescription] = React.useState(() => '');
  const [descriptionEnding, setDescriptionEnding] = React.useState(() => '');
  const [screenshotImage, setScreenshotImage] = React.useState(
    () => displayNameScreenshot,
  );
  const [footerInfo, setFooterInfo] = React.useState(() => '');
  const [xlink, setXlink] = React.useState(() => '');
  const [linkedinLink, setLinkedinLink] = React.useState(() => '');
  const [instagramLink, setInstagramLink] = React.useState(() => '');
  const [saveLoading, setSaveLoading] = React.useState(() => false);
  const [submitted, setSubmitted] = React.useState(false);
  const toast = React.useRef<Toast>(null);
  const getSettigs = React.useCallback(async () => {
    setSaveLoading(true);

    try {
      const res = await OpenAPI.GET('/api/v2/admin/email-setting', {
        params: { query: { tenantId } },
      });

      setEmailSettings(res.data);
    } catch {}

    setSaveLoading(false);
  }, [tenantId]);

  useEffect(() => {
    getSettigs().catch(noop);
  }, [getSettigs]);

  useEffect(() => {
    if (emailSettings) {
      setDisplayName(emailSettings.displayName ?? '');
      setDescriptionTitle(emailSettings.descriptionTitle ?? '');
      setDescription(emailSettings.description ?? '');
      setDescriptionEnding(emailSettings.descriptionEnding ?? '');
      setFooterInfo(emailSettings.footerInfo ?? '');
      setXlink(emailSettings.xlink ?? '');
      setLinkedinLink(emailSettings.linkedinLink ?? '');
      setInstagramLink(emailSettings.instagramLink ?? '');
    }
  }, [emailSettings]);

  async function saveData() {
    setSaveLoading(true);
    setSubmitted(true);
    if (
      description.trim().length > 0 &&
      descriptionEnding.trim().length > 0 &&
      displayName.trim().length > 0 &&
      footerInfo.trim().length > 0 &&
      descriptionTitle.trim().length > 0
    )
      try {
        const res = await OpenAPI.POST('/api/v2/admin/email-setting', {
          params: {
            query: {
              tenantId,
            },
          },
          body: {
            description,
            descriptionEnding,
            displayName,
            footerInfo,
            descriptionTitle,
            instagramLink: normalizeString(instagramLink),
            linkedinLink: normalizeString(linkedinLink),
            xlink: normalizeString(xlink),
          },
        });

        // @ts-expect-error
        if (res.error) throw res.error;

        toast.current?.show({
          severity: 'success',
          summary: `Saved successfully`,
          life: 3000,
        });
        setSubmitted(false);
      } catch (err) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error occurred',
          detail:
            typeof err === 'string'
              ? err
              : err instanceof Error
                ? err.message
                : (err as CustomError)?.message || 'Unknown error',
          life: 3000,
        });
      }

    setSaveLoading(false);
  }

  return (
    <div>
      {saveLoading ? (
        <Skeleton width="100rem" height="100rem" />
      ) : (
        <div>
          <div className="gap-4">
            <div className="flex flex-row w-full items-start gap-4 max-md:flex-col max-md:gap-8 max-md:items-center">
              <div className="flex flex-col mr-4 w-[50%] max-xl:w-[90%] max-w-[512px] bg-zinc-700 rounded-3xl max-md:mr-0 max-md:w-full max-md:max-w-[320px]">
                <img src={screenshotImage} />
              </div>

              <div className="flex flex-col justify-center w-full px-6 py-8 rounded-3xl shadow-[0px_4px_12px_rgba(0,0,0,0.1)] text-base leading-6 text-black bg-white">
                <div className="text-3xl font-medium">Welcome Email</div>

                <div className="">
                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Display Name (required)</div>
                    <InputText
                      value={displayName}
                      onChange={(e) => {
                        setDisplayName(e.target.value);
                      }}
                      onFocus={() => {
                        setScreenshotImage(displayNameScreenshot);
                      }}
                      placeholder=""
                      className={`mt-3 p-inputtext-lg w-full  ${submitted && !displayName ? 'p-invalid' : ''}`}
                    />
                  </div>

                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Description Title (required)</div>
                    <InputText
                      value={descriptionTitle}
                      onChange={(e) => {
                        setDescriptionTitle(e.target.value);
                      }}
                      onFocus={() => {
                        setScreenshotImage(descriptionTitleScreenshot);
                      }}
                      placeholder=""
                      className={`mt-3 p-inputtext-lg w-full  ${submitted && !descriptionTitle ? 'p-invalid' : ''}`}
                    />
                  </div>
                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Description (required)</div>
                    <InputTextarea
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                      }}
                      onFocus={() => {
                        setScreenshotImage(descriptionScreenshot);
                      }}
                      className={`mt-3 p-inputtext-lg w-full  ${submitted && !description ? 'p-invalid' : ''}`}
                    />
                  </div>
                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Description Ending (required)</div>
                    <InputText
                      value={descriptionEnding}
                      onChange={(e) => {
                        setDescriptionEnding(e.target.value);
                      }}
                      onFocus={(e) => {
                        setScreenshotImage(descriptionEndingScreenshot);
                      }}
                      className={`mt-3 p-inputtext-lg w-full  ${submitted && !descriptionEnding ? 'p-invalid' : ''}`}
                    />
                  </div>
                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Footer (required)</div>
                    <InputText
                      value={footerInfo}
                      onChange={(e) => {
                        setFooterInfo(e.target.value);
                      }}
                      onFocus={(e) => {
                        setScreenshotImage(footerInfoScreenshot);
                      }}
                      className={`mt-3 p-inputtext-lg w-full  ${submitted && !footerInfo ? 'p-invalid' : ''}`}
                    />
                  </div>
                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>X link</div>
                    <InputText
                      value={xlink}
                      onChange={(e) => {
                        setXlink(e.target.value);
                      }}
                      onFocus={(e) => {
                        setScreenshotImage(xlinkScreenshot);
                      }}
                      className="mt-3 p-inputtext-lg w-full"
                    />
                  </div>
                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Instagram link</div>
                    <InputText
                      value={instagramLink}
                      onChange={(e) => {
                        setInstagramLink(e.target.value);
                      }}
                      onFocus={(e) => {
                        setScreenshotImage(instagramLinkScreenshot);
                      }}
                      className="mt-3 p-inputtext-lg w-full"
                    />
                  </div>
                  <div className="w-[40%] max-w-xl min-w-96">
                    <div>Linkedin Link</div>
                    <InputText
                      value={linkedinLink}
                      onChange={(e) => {
                        setLinkedinLink(e.target.value);
                      }}
                      onFocus={(e) => {
                        setScreenshotImage(linkedinLinkScreenshot);
                      }}
                      className="mt-3 p-inputtext-lg w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-row w-full px-6 py-6 justify-end">
            <Button
              label="Save"
              className={`text-2xl px-14 py-3 bg-[#F1B51C] hover:bg-bluegray-400 text-black border-none`}
              onClick={saveData}
            />
          </div>
        </div>
      )}

      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}

export default Emails;
